"use client"
import { useQualityLearning } from "@/hooks/use-quality-presentations"


import { Fragment, useState, type ReactNode } from "react"
import { TriangleAlert } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useSetSelfLearningModeMutation } from "@/hooks/mutations/self-learning"
import { useSelfLearningQuery } from "@/hooks/queries/self-learning"
import {
  SELF_LEARNING_MODES,
  SelfLearningModeSchema,
  type SelfLearningMode,
} from "@/lib/conversation-quality/self-learning"
import { type SelfLearningCopy } from "@/lib/conversation-quality/self-learning-display"
import { cn } from "@/lib/utils"

import { FullAutopilotConfirmDialog } from "./full-autopilot-confirm-dialog"

/**
 * An agent's Self-learning mode as a Select, each mode described in its list. Full autopilot is set only through its
 * confirmation; read-only without `canManage`, locked while the feature is off.
 */
export function SelfLearningModeSelect({
  agentId,
  agentName,
  canManage,
  id,
  describedBy,
  triggerClassName,
  fallback = null,
}: {
  agentId: string
  agentName: string
  canManage: boolean
  /** For the label beside it. */
  id: string
  describedBy?: string
  triggerClassName?: string
  /** Until the agent's setting is read. */
  fallback?: ReactNode
}) {
  const { SELF_LEARNING_COPY } = useQualityLearning()

  const { data } = useSelfLearningQuery(agentId)
  const save = useSetSelfLearningModeMutation(agentId)
  const [confirmingFull, setConfirmingFull] = useState(false)

  if (!data) return fallback

  const choose = (mode: SelfLearningMode) => {
    if (mode === "full") setConfirmingFull(true)
    else save.mutate({ input: { mode } })
  }

  return (
    <>
      <Select
        value={data.mode}
        onValueChange={(next) => {
          const mode = SelfLearningModeSchema.safeParse(next)
          if (mode.success) choose(mode.data)
        }}
        disabled={!canManage || data.qualityDisabledBy !== null || save.isPending}
      >
        <SelectTrigger id={id} aria-describedby={describedBy} className={cn("bg-background", triggerClassName)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent
          align="end"
          className="max-h-[min(24rem,var(--radix-select-content-available-height))] max-w-[min(32rem,calc(100vw-2rem))]"
        >
          {SELF_LEARNING_MODES.map((mode, index) => (
            <Fragment key={mode}>
              {index > 0 && <SelectSeparator className="bg-border" />}
              <SelectItem value={mode} description={<ModeDetails copy={SELF_LEARNING_COPY[mode]} />}>
                {SELF_LEARNING_COPY[mode].label}
              </SelectItem>
            </Fragment>
          ))}
        </SelectContent>
      </Select>
      <FullAutopilotConfirmDialog
        agentId={agentId}
        agentName={agentName}
        open={confirmingFull}
        onOpenChange={setConfirmingFull}
      />
    </>
  )
}

function ModeDetails({ copy }: { copy: SelfLearningCopy }) {
  const { RECOMMENDED_BADGE } = useQualityLearning()

  return (
    <span className="flex flex-col items-start gap-1">
      {copy.recommended && <Badge variant="secondary">{RECOMMENDED_BADGE}</Badge>}
      <span>{copy.description}</span>
      {copy.warning && <ModeWarning warning={copy.warning} />}
    </span>
  )
}

export function ModeWarning({ warning }: { warning: NonNullable<SelfLearningCopy["warning"]> }) {
  return (
    <span
      className={cn(
        "flex items-start gap-1.5 text-xs font-medium",
        warning.tone === "danger" ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400"
      )}
    >
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span>{warning.text}</span>
    </span>
  )
}
